import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import CampaignContactTable from '../components/campaigns/CampaignContactTable';
import CampaignStats from '../components/campaigns/CampaignStats';
import AddContactsModal from '../components/campaigns/AddContactsModal';
import { getCampaignById, getCampaignContacts, addCampaignContact, updateCampaignContact, removeCampaignContact, getGifts } from '../utils/apiService';

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddContactsModal, setShowAddContactsModal] = useState(false);
  const [allAlumni, setAllAlumni] = useState([]);
  const [activeTab, setActiveTab] = useState('contacts');
  const [giftHistory, setGiftHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCampaignData();
  }, [id]);

  const loadCampaignData = async () => {
    try {
      setLoading(true);
      const [campaignData, contactsData] = await Promise.all([
        getCampaignById(id),
        getCampaignContacts(id)
      ]);
      setCampaign(campaignData);
      setContacts(contactsData);
      setError(null);
    } catch (err) {
      setError('Failed to load campaign data. Please try again.');
      console.error('Error loading campaign data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load all alumni for adding contacts
  useEffect(() => {
    const loadAllAlumni = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCampaignContacts(id);
        setAllAlumni(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadAllAlumni();
  }, [id]);

  // Load gift history for this campaign
  useEffect(() => {
    const loadGiftHistory = async () => {
      if (!campaign?.sendosoEnabled) return;
      
      setLoading(true);
      setError(null);
      try {
        const data = await getGifts();
        // Filter gifts for this campaign
        const campaignGifts = data.filter(gift => gift.campaignId === id);
        setGiftHistory(campaignGifts);
      } catch (err) {
        setError('Failed to load gift history. Please try again.');
        console.error('Error loading gift history:', err);
      } finally {
        setLoading(false);
      }
    };

    if (campaign?.sendosoEnabled) {
      loadGiftHistory();
    }
  }, [id, campaign?.sendosoEnabled]);

  // Handle adding a contact to the campaign
  const handleAddContact = async (contact) => {
    setLoading(true);
    setError(null);
    try {
      const data = await addCampaignContact(id, contact);
      setContacts(prev => [...prev, data]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle updating a contact
  const handleUpdateStatus = async (contactId, updates) => {
    try {
      const updatedContact = await updateCampaignContact(id, contactId, updates);
      setContacts(contacts.map(contact => 
        contact._id === contactId ? updatedContact : contact
      ));
    } catch (err) {
      setError('Failed to update contact status. Please try again.');
      console.error('Error updating contact status:', err);
    }
  };

  // Handle removing a contact
  const handleRemoveContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to remove this contact from the campaign?')) {
      return;
    }

    try {
      const response = await removeCampaignContact(id, contactId);
      if (response.success) {
        // Update the contacts list
        setContacts(contacts.filter(contact => contact._id !== contactId));
        // Reload campaign data to update counts
        await loadCampaignData();
      } else {
        throw new Error(response.error || 'Failed to remove contact');
      }
    } catch (err) {
      setError('Failed to remove contact: ' + err.message);
      console.error('Error removing contact:', err);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Campaign not found</h2>
          <p className="mt-2 text-gray-600">The campaign you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/app/campaigns')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Campaign Header */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
              <p className="mt-1 text-sm text-gray-600">{campaign.description}</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
              </span>
              <button
                onClick={() => navigate('/app/campaigns')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Back to Campaigns
              </button>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50">
          <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Start Date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(campaign.startDate).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">End Date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(campaign.endDate).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Target Count</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {campaign.targetCount} contacts
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'contacts' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('contacts')}
          >
            Contacts ({contacts.length})
          </button>
          <button
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'stats' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('stats')}
          >
            Statistics
          </button>
          {campaign.sendosoEnabled && (
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'gifts' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('gifts')}
            >
              Gift History ({giftHistory.length})
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'contacts' ? (
        <CampaignContactTable 
          contacts={contacts}
          campaign={campaign}
          onUpdateStatus={handleUpdateStatus}
          onRemoveContact={handleRemoveContact}
        />
      ) : activeTab === 'stats' ? (
        <CampaignStats 
          campaign={campaign}
          contacts={contacts}
        />
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-medium text-gray-800">Sendoso Gift History</h3>
            <p className="text-sm text-gray-600">Track all physical gifts sent to contacts in this campaign</p>
          </div>
          
          {giftHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gift Type</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {giftHistory.map((gift) => (
                    <tr key={gift.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{gift.contactName}</div>
                        <div className="text-sm text-gray-500">{gift.contactEmail}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{gift.giftType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(gift.scheduledDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {gift.status.charAt(0).toUpperCase() + gift.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(gift.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center">
              <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-800 mb-1">No Gifts Sent Yet</h3>
              <p className="text-gray-600 mb-4">Start sending gifts to your contacts by clicking the gift icon in the contacts tab.</p>
            </div>
          )}
        </div>
      )}

      {/* Add Contacts Modal */}
      {showAddContactsModal && (
        <AddContactsModal
          onClose={() => setShowAddContactsModal(false)}
          onSave={handleAddContact}
          alumni={allAlumni}
          existingContactIds={contacts.map(c => c.id)}
        />
      )}
    </div>
  );
};

export default CampaignDetail;
